import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Observable } from 'rxjs/internal/Observable';

import { EmailAddressService } from './email-address.service';

@Injectable({ providedIn: 'root' })
export class YeehroCalendlyBookingService {
  private API_URL = '/api/yeehro-calendly-bookings/';
  private headerOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  // Booking context — carries prefill, relatedRecord, inviteId across route navigation.
  // Set from route component on init (reads params → API → context) or from CRM navigation.
  // Read synchronously via getBookingContext() — no subscriptions.
  private _bookingContext = new BehaviorSubject<any>(null);
  setBookingContext(ctx: any) { this._bookingContext.next(ctx); }
  getBookingContext(): any { return this._bookingContext.getValue(); }

  // CRM record types that can be booked against — the four "person" modules
  // resolveRecordPrefill / openBookMeetingFlow support. Single source of truth
  // for any UI gate, type picker, or eligibility check.
  public readonly bookableRecordModules: string[] = ['leads', 'contacts', 'subs', 'providers'];

  constructor(
    private httpClient: HttpClient,
    private _emailAddressService: EmailAddressService
  ) {}


  // hostUsername scopes a public template's slots to that host's schedule (the
  // booking page's :username). Ignored for the host's own events.
  // hostId (case 1502): in-app screens send the host's id instead; wins over hostUsername.
  getAvailableSlots(eventId: string, month: string, hostUsername: string = '', hostId: string = '', rescheduleOf: string = ''): Observable<any> {
    let url = this.API_URL + 'available-slots/' + eventId + '?month=' + month;
    if (hostId) url += '&hostId=' + encodeURIComponent(hostId);
    else if (hostUsername) url += '&host=' + encodeURIComponent(hostUsername);
    if (rescheduleOf) url += '&rescheduleOf=' + encodeURIComponent(rescheduleOf); // Case 1502: existing meeting being moved
    return this.httpClient.get<any>(url);
  }


  getHostBookingsInRange(hostId: string, start: Date, end: Date): Observable<any[]> {
    const params = `?start=${encodeURIComponent(start?.toISOString() || '')}&end=${encodeURIComponent(end?.toISOString() || '')}`;
    return this.httpClient.get<any[]>(this.API_URL + 'host/' + hostId + '/range' + params);
  }


  // hostId (case 1502): returns that host's hours for a shared template instead of the author's.
  getScheduleForEvent(eventId: string, hostId: string = ''): Observable<any> {
    const params = hostId ? '?hostId=' + encodeURIComponent(hostId) : '';
    return this.httpClient.get<any>(this.API_URL + 'schedule/' + eventId + params);
  }


  // ── Public (no auth) ──────────────────────────────────


  resolvePublicEvent(username: string, slug: string): Observable<any> {
    return this.httpClient.get<any>(this.API_URL + 'public/resolve/' + username + '/' + slug);
  }


  // excludeLocationTypes: optional list backend uses to drop events whose ALL
  // locations are in the list (mixed events keep showing). Callers get the
  // list from getExcludedLocationTypesForBooking() on the event service.
  getPublicEvents(username: string, excludeLocationTypes: string[] = []): Observable<any> {
    let url = this.API_URL + 'public/events/' + username;
    if (excludeLocationTypes?.length) {
      url += '?excludeLocationTypes=' + encodeURIComponent(excludeLocationTypes.join(','));
    }
    return this.httpClient.get<any>(url);
  }


  // Public confirmation-page fallback. Both single-host and aggregate
  // confirmation flows call this when their shared booking-service context
  // is empty (typically a refresh on the /invitees/:bookingId route by an
  // unauthenticated invitee). Returns a slimmed booking record.
  getPublicById(id: string): Observable<any> {
    return this.httpClient.get<any>(this.API_URL + 'public/by-id/' + id);
  }


  // ── Invitee self-service (case 1476) ──────────────────
  // Token-keyed, no session. Response: { booking, canModify, lockReason,
  // lockMessage, cutoffMinutes, hostUsername }.
  getManageBooking(token: string): Observable<any> {
    return this.httpClient.get<any>(this.API_URL + 'public/manage/' + encodeURIComponent(token));
  }


  cancelByManageToken(token: string, cancelReason: string = ''): Observable<any> {
    const body = { cancelReason: cancelReason || '' };
    return this.httpClient.post<any>(this.API_URL + 'public/manage/' + encodeURIComponent(token) + '/cancel', JSON.stringify(body), this.headerOptions);
  }


  rescheduleByManageToken(token: string, start: Date, end: Date): Observable<any> {
    const body = { start: start?.toISOString(), end: end?.toISOString() };
    return this.httpClient.post<any>(this.API_URL + 'public/manage/' + encodeURIComponent(token) + '/reschedule', JSON.stringify(body), this.headerOptions);
  }


  // Aggregate ("open") booking flow — one call returns rolling-horizon slots
  // merged across the recruiter pool. Response shape:
  //   { days: ['YYYY-MM-DD', ...], slotsByDay: { 'YYYY-MM-DD': [{ time, hostIds: [] }, ...] } }
  // hostIds carries every recruiter available for that slot so the frontend can
  // pick one at commit time and fall back on the next id if the write races.
  getAggregateSlots(): Observable<any> {
    return this.httpClient.get<any>(this.API_URL + 'public/aggregate-slots');
  }


  // Direct ("assigned-only") slot fetch. Sibling of getAggregateSlots. Powers
  // the /booking/direct-reservation flow. Requires an inviteToken - the
  // backend derives the assigned recruiter from the invite's related record.
  // Response shape:
  //   { hostId, event, days, slotsByDay, timezone }
  // Well-defined error kinds returned on non-2xx: inviteMissing / inviteNotFound
  // / inviteAlreadyUsed / noAssignee - frontend maps each to the right message.
  getDirectSlots(inviteToken: string): Observable<any> {
    const url = this.API_URL + 'public/direct-slots?inviteToken=' + encodeURIComponent(inviteToken);
    return this.httpClient.get<any>(url);
  }


  // Aggregate ("open") booking commit. Server picks a winner from hostIds,
  // resolves their 15-min phone event (find or clone), and creates the
  // booking. Payload is the standard bookingData shape from booking-form's
  // buildBookingData, plus hostIds + inviteToken added by the aggregate
  // reservation's submitOverride wrapper.
  aggregateBook(payload: any): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'public/aggregate-book', JSON.stringify(payload), this.headerOptions);
  }


  getAll(): Observable<any[]> {
    return this.httpClient.get<any[]>(this.API_URL);
  }


  getById(id: string): Observable<any> {
    return this.httpClient.get<any>(this.API_URL + id);
  }


  create(booking: any): Observable<any> {
    return this.httpClient.post<any>(this.API_URL, JSON.stringify(booking), this.headerOptions);
  }


  // Send invitee + recruiter (when applicable) confirmation emails, create
  // Message + Activity records, and create the recruiter calendar event.
  // Backend determines the policy from booking.creationMethod.
  //
  // additionalRecipients (optional): direct flow uses it for owner heads-up
  // and other courtesy copies. Each entry is {userId, role?}; backend skips
  // any that match the winning host. Callers omitting this get identical
  // behavior to before.
  // Public route — no auth required (called from public booking page too).
  sendBookingConfirmations(booking: any, additionalRecipients?: any[]): Observable<any> {
    const payload: any = { booking: booking };
    if (Array.isArray(additionalRecipients) && additionalRecipients.length) {
      payload.additionalRecipients = additionalRecipients;
    }
    return this.httpClient.post<any>(this.API_URL + 'send-confirmations', JSON.stringify(payload), this.headerOptions);
  }


  update(booking: any): Observable<any> {
    return this.httpClient.put<any>(this.API_URL + booking._id, JSON.stringify(booking), this.headerOptions);
  }


  // Reassign a booking to a new host, optionally on a new time slot. Body:
  // { newHostId, newStart?, newEnd?, note? }. Backend runs the match/clone
  // on the new host's events, updates the booking (host, event, location,
  // optional start/end), and returns the updated record. Frontend follows
  // with processReassignNotification via the workflow service.
  reassign(bookingId: string, payload: { newHostId: string; newStart?: Date | null; newEnd?: Date | null; note?: string }): Observable<any> {
    const body: any = {
      newHostId: payload?.newHostId,
      note: payload?.note || ''
    };
    if (payload?.newStart) body.newStart = new Date(payload.newStart).toISOString();
    if (payload?.newEnd) body.newEnd = new Date(payload.newEnd).toISOString();
    return this.httpClient.post<any>(this.API_URL + bookingId + '/reassign', JSON.stringify(body), this.headerOptions);
  }


  // Pre-check for the reassign dialog. Returns { matchType, matchedEvent,
  // wouldCreateEvent, locationChanges, availableAtTime } for the given new
  // host + optional new time. Powers the green/amber status line.
  reassignPreview(bookingId: string, newHostId: string, newStart?: Date | string | null): Observable<any> {
    let url = this.API_URL + bookingId + '/reassign-preview?newHostId=' + encodeURIComponent(newHostId);
    if (newStart) {
      const iso = newStart instanceof Date ? newStart.toISOString() : String(newStart);
      url += '&newStart=' + encodeURIComponent(iso);
    }
    return this.httpClient.get<any>(url);
  }


  // Dashboard counters. Pass a user (full record or _id), or null for system-wide.
  // tz is auto-detected from the browser so today/week boundaries match the viewer.
  getDashboardCounts(user: any): Observable<any> {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    const body = { "user": user, "tz": tz };
    return this.httpClient.post<any>(this.API_URL + 'dashboard-counts', JSON.stringify(body), this.headerOptions);
  }


  // Reporting rollup — sys-admin only on the backend. Returns summary + per-host,
  // over-time, and top-template breakdowns for the given range.
  getReports(start: Date, end: Date): Observable<any> {
    const body = { start: start?.toISOString(), end: end?.toISOString() };
    return this.httpClient.post<any>(this.API_URL + 'reports', JSON.stringify(body), this.headerOptions);
  }


  delete(id: string): Observable<any> {
    return this.httpClient.delete<any>(this.API_URL + id);
  }


  search(searchTerms: any): Observable<any[]> {
    return this.httpClient.post<any[]>(this.API_URL + 'search', JSON.stringify(searchTerms), this.headerOptions);
  }


  getCustomDatasource(searchParams: any, sortField = 'start', sortDirection = 'desc', pageNumber: number = 0, pageSize: number = 25): Observable<any> {
    searchParams['selectAll'] = false;
    searchParams.sortField = sortField;
    searchParams.sortOrder = sortDirection;
    searchParams.pageNumber = pageNumber;
    searchParams.pageSize = pageSize;
    return this.httpClient.post<any>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchParams), this.headerOptions);
  }


  // Returns every booking matching searchParams (no pagination). Backend
  // branches off the selectAll flag inside dataSourceSearch.
  selectAllSearch(searchParams: any): Observable<any> {
    searchParams['selectAll'] = true;
    return this.httpClient.post<any>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchParams), this.headerOptions);
  }


  // Cursor-based paged fetch — drift-resistant alternative to getCustomDatasource.
  // Pass cursor=null for the first page; pass back the response's `cursor` value
  // for subsequent calls. Returns: { message: records[], cursor, hasMore, totalItemCount }.
  // totalItemCount is only populated on the first call (cursor=null).
  getCursorPage(searchParams: any, sortField = 'start', sortDirection = 'desc', cursor: any = null, pageSize: number = 25): Observable<any> {
    const body = {
      ...searchParams,
      sortField: sortField,
      sortOrder: sortDirection,
      cursor: cursor,
      pageSize: pageSize
    };
    return this.httpClient.post<any>(this.API_URL + 'cursor-search', JSON.stringify(body), this.headerOptions);
  }


  // ── Shared booking helpers (used by both internal + public components) ──


  // Status checks — pure functions on a booking object. Used by booking-meeting-item,
  // booking-grid, booking-profile to keep status logic consistent across the app.
  isPast(booking: any): boolean {
    const end = booking?.end ? new Date(booking.end) : null;
    if (!end || isNaN(end.getTime())) return false;
    return end < new Date();
  }


  isCancelled(booking: any): boolean {
    return booking?.status === 'cancelled' || booking?.active === false;
  }


  // True if ANY invitee on the booking has been marked a no-show. Used for the
  // grid pill + filter ("show me no-show meetings") — answers "did the meeting
  // contain a no-show outcome" regardless of which invitee was marked.
  isNoShow(booking: any): boolean {
    return Array.isArray(booking?.invitees) && booking.invitees.some((i: any) => i?.noShow === true);
  }


  // Per-invitee no-show check — drives the inline button state in the
  // booking-profile People section.
  isInviteeNoShow(invitee: any): boolean {
    return invitee?.noShow === true;
  }


  // Status label for the pill. Precedence: No-show > Cancelled > Past > Upcoming.
  // No-show wins display-wise because it tells you WHY a record is the way it
  // is — for a single-invitee no-show, the booking is also cancelled, but
  // "No-show" is more informative than "Cancelled" at a glance.
  getStatusLabel(booking: any): string {
    if (this.isNoShow(booking)) return 'No-show';
    if (this.isCancelled(booking)) return 'Cancelled';
    if (this.isPast(booking)) return 'Past';
    return 'Upcoming';
  }


  // Action visibility — upcoming + active means Reschedule / Cancel are offered.
  showRescheduleCancel(booking: any): boolean {
    return !this.isPast(booking) && !this.isCancelled(booking);
  }


  // Past + not cancelled + the given invitee isn't already flagged → show
  // "Mark No-Show" for that invitee. Takes the booking for the past/cancelled
  // gates and the invitee for the per-row check.
  showMarkInviteeNoShow(booking: any, invitee: any): boolean {
    return this.isPast(booking) && !this.isCancelled(booking) && !this.isInviteeNoShow(invitee);
  }


  // Past + invitee already marked → show "Undo No-Show" for that invitee.
  // Cancelled bookings are excluded — once a single-invitee booking flips to
  // cancelled via no-show, undo goes through the same per-invitee path which
  // re-activates the booking server-side.
  showUndoInviteeNoShow(booking: any, invitee: any): boolean {
    return this.isPast(booking) && this.isInviteeNoShow(invitee);
  }


  isEmailValid(email: string): boolean {
    if (!email?.trim()) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }


  // Phone fields use a US-format input mask (XXX-XXX-XXXX) that pads unfilled
  // positions with '_'. Empty-only checks miss "555-010-0___" — strip non-digits
  // and require exactly 10 to ensure the field is actually complete.
  isPhoneComplete(phone: string): boolean {
    if (!phone) return false;
    const digits = String(phone).replace(/\D/g, '');
    return digits.length === 10;
  }


  areRequiredQuestionsAnswered(answers: any[]): boolean {
    for (const a of answers || []) {
      if (!a?.required) continue;
      if (a?.type === 'radio-buttons') {
        if (!a?.answer?.trim()) return false;
        // If "Other" is selected, the text must be filled in
        if (a.answer === '__other__' && !a?.otherText?.trim()) return false;
      } else if (a?.type === 'checkboxes') {
        const hasSelection = a?.selectedOptions?.length || a?.otherText?.trim();
        if (!hasSelection) return false;
      } else if (a?.type === 'phone-number') {
        // Phone questions use the masked input — strict 10-digit check, not just non-empty.
        if (!this.isPhoneComplete(a?.answer)) return false;
      } else if (!a?.answer?.trim()) {
        return false;
      }
    }
    return true;
  }


  /**
   * Catches shape-level invalid answers regardless of required flag:
   *  - "Other" radio selected without text in the inline input.
   *  - Phone-number question with masked-incomplete value (partial digits +
   *    underscores from the input mask). Empty optional phone is fine.
   */
  areCustomAnswersValid(answers: any[]): boolean {
    for (const a of answers || []) {
      if (a?.type === 'radio-buttons' && a?.answer === '__other__' && !a?.otherText?.trim()) {
        return false;
      }
      if (a?.type === 'phone-number' && a?.answer && !this.isPhoneComplete(a.answer)) {
        // Has a value but isn't a complete phone — reject (catches "555-010-0___").
        return false;
      }
    }
    return true;
  }


  validateBookingTimes(start: Date | null, end: Date | null): boolean {
    if (!start || !end) return false;
    if (end <= start) return false;
    if (start.getTime() <= Date.now()) return false;
    return true;
  }


  // Emits the saved customAnswers payload from the form's working state.
  // Each entry is emitted in its natural shape per question type:
  //   - checkboxes → array of strings (selected options + Other text if any)
  //   - radio-buttons → string (the picked option, or the typed Other text)
  //   - everything else → string
  // Defensive throughout: non-string fields are coerced, missing fields fall
  // back to safe defaults, and empty values (per shape) are filtered out so
  // the booking never carries useless zero-content entries.
  buildCustomAnswersPayload(answers: any[]): any[] {
    return (answers || [])
      .map((a: any) => {
        const type = a?.type || 'one-line';
        let answerValue: any = '';

        if (type === 'checkboxes') {
          // Multi-select natural shape: array of selected option texts +
          // appended "Other" text when the user typed one. Both inputs are
          // defended against non-array / non-string shapes.
          const selected = Array.isArray(a?.selectedOptions) ? a.selectedOptions : [];
          const otherText = (typeof a?.otherText === 'string') ? a.otherText.trim() : '';
          const collected = selected
            .map((v: any) => (typeof v === 'string') ? v.trim() : '')
            .filter((v: string) => v.length > 0);
          if (otherText) collected.push(otherText);
          answerValue = collected;
        } else if (type === 'radio-buttons') {
          // Single-select with optional Other. When the picked option is the
          // sentinel '__other__', save the user-typed text directly so the
          // saved string is human-meaningful. Otherwise save the picked option text.
          const rawAnswer = (typeof a?.answer === 'string') ? a.answer : '';
          const otherText = (typeof a?.otherText === 'string') ? a.otherText.trim() : '';
          answerValue = rawAnswer === '__other__' ? otherText : rawAnswer.trim();
        } else {
          // one-line / multiple-lines / phone-number / dropdown → plain trimmed string
          const rawAnswer = (typeof a?.answer === 'string') ? a.answer : '';
          answerValue = rawAnswer.trim();
        }

        const question = (typeof a?.question === 'string') ? a.question.trim() : '';
        return { question: question, type: type, answer: answerValue };
      })
      .filter((a: any) => {
        // Skip entries with no question or no meaningful answer (per shape).
        if (!a?.question) return false;
        const v = a?.answer;
        if (v == null || v === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
      });
  }


  // Normalizes a saved customAnswer value into a human-readable string for
  // display. Handles every shape Mongoose's Mixed type can store:
  //   - string  → as-is, trimmed
  //   - array   → comma-joined non-empty entries (checkboxes / multi-select)
  //   - boolean → "Yes" / "No" (yes-no)
  //   - number  → numeric string (rating, etc.)
  //   - null / undefined / empty → ''
  // Caller should treat '' as "no meaningful answer" and skip rendering.
  formatAnswerValue(value: any): string {
    if (value == null) return '';
    if (Array.isArray(value)) return value.filter((v: any) => v != null && v !== '').map((v: any) => String(v)).join(', ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    return String(value).trim();
  }


  getBookingErrorMessage(err: any): string {
    // Pull the human-readable reason defensively — server may put it on
    // err.error.message (parsed JSON), err.error (raw string body), err.message
    // (transport/network error), or err itself may already BE the message string
    // when the app-level ErrorInterceptor has flattened the HttpErrorResponse.
    // Surface it so the user sees the actual cause (meeting-limit reached,
    // conflicting booking, past time, etc.) instead of a generic fallback.
    const serverMsg = err?.error?.message
      || (typeof err?.error === 'string' ? err.error : null)
      || (typeof err === 'string' ? err : null)
      || err?.message;

    // Use isConflictError so 409s are recognised via either the status field
    // OR the flattened message string — keeps the friendly "no longer available"
    // wording even after ErrorInterceptor strips the response status.
    if (this.isConflictError(err)) {
      return serverMsg || 'This time slot is no longer available. Please select a different time.';
    }
    if (err?.status === 400) {
      return serverMsg || 'Invalid booking data. Please check all fields and try again.';
    }
    return serverMsg || 'An error occurred while booking. Please try again.';
  }


  isConflictError(err: any): boolean {
    // Preferred path: HttpErrorResponse still has its status. Falls through
    // when a callable HttpClient reaches us untouched.
    if (err?.status === 409) return true;
    // Fallback: the app-level ErrorInterceptor strips non-2xx responses down
    // to just their message string before booking-form catches - status is
    // gone by then. Match on the copy the backend actually returns for 409s
    // in saveBooking + aggregateBook so both flows recognise the race and
    // reset correctly. Covered cases:
    //   - "This time slot is no longer available. Please select a different time."
    //   - "This time overlaps with an existing booking."
    //   - "The office is closed for X." (holiday full-close)
    //   - "X is a half-day. Bookings must end by Y." (holiday half-day)
    const asStr = typeof err === 'string' ? err : (err?.message || '');
    return /(no longer available|overlaps with an existing booking|office is closed|is a half-day)/i.test(String(asStr || ''));
  }


  // ── Record → prefill normalization ──────────────────────
  //
  // Normalizes a CRM record (Lead/Contact/Sub/Provider) into the field shape our
  // booking + invite flows need. Both internal booking and scheduling-invite
  // reuse this via `openBookMeetingFlow` / `openShareAvailabilityFlow`. If more
  // record types get the "Send scheduling invite" button later, add a case here.


  // Single resolver for every record type — leads, contacts, subs, providers all
  // share the same field names (first_name, last_name, mobile_phone, etc.), so
  // there's no value in routing to per-type methods. If a future type ever needs
  // genuinely different mapping, branch HERE on a derived recordType. Until then
  // one function covers everything.
  //
  // Every value is funneled through `asString` so corrupt fields (numbers,
  // objects, anything non-string) can't crash the resolver — `.trim()` and
  // string coercion are safe regardless of input shape.
  resolveRecordPrefill(record: any): {
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    timezone?: string;
    address?: string;
  } {
    if (!record) return {};
    const firstName = this.asString(record?.first_name);
    const lastName = this.asString(record?.last_name);
    const phone =
      this.asString(record?.mobile_phone) ||
      this.asString(record?.phone_mobile) ||
      this.asString(record?.home_phone) ||
      this.asString(record?.phone);
    return {
      firstName: firstName || undefined,
      lastName:  lastName || undefined,
      name:      this.buildFullName(firstName, lastName, this.asString(record?.name)),
      email:     this.extractPrimaryEmail(record),
      phone:     phone || '',
      timezone:  this.asString(record?.timezone),
      address:   this.extractAddress(record)
    };
  }


  // Resolves the form-prefill values for the reschedule flow. Pure function,
  // safe to call from anywhere (dialog init, future automation, etc.).
  //
  // Strategy:
  //   - Snapshot fields (name, duration, location) come from the CURRENT event
  //     so any host edits since the original booking are reflected.
  //   - customAnswers: reconciled against current event.customQuestions —
  //     keep matching answers (by question text), drop orphans (questions
  //     removed from the event), leave new questions empty for the user.
  //   - Invitee fields (name/email/phone/timezone) come from the BOOKING
  //     itself — same person being rescheduled, no need to re-pull.
  //   - start / end intentionally NOT prefilled — user picks a new time.
  //     Former time is shown in the dialog header instead.
  resolveReschedulePrefill(booking: any, currentEvent: any): {
    meetingName: string;
    duration: number;
    location: any;
    customAnswers: any[];
    inviteeName: string;
    inviteeEmail: string;
    inviteePhone: string;
    calendarTimezone: string;
  } {
    const evt = currentEvent || {};
    const invitee = booking?.invitees?.[0] || {};

    // ── Q&A pass-through ──────────────────────────────────
    // Returns each prior answer raw — preserves its actual shape (string,
    // array, etc.) so applyReschedulePrefill can distribute it correctly into
    // the form per the question's current type. `savedType` carries the type
    // snapshot from the saved booking; may be missing for legacy data, in
    // which case the form trusts the event's current type as authoritative.
    // The matching of saved → current question, validation, and form-shape
    // distribution all happen in applyReschedulePrefill.
    const priorAnswers = Array.isArray(booking?.customAnswers) ? booking.customAnswers : [];
    const reconciledAnswers = priorAnswers.map((pa: any) => ({
      question: this.asString(pa?.question),
      savedType: pa?.type,
      answer: pa?.answer
    }));

    return {
      meetingName: this.asString(evt?.name) || this.asString(booking?.name),
      duration:    Number(evt?.duration || booking?.duration || 30),
      location:    evt?.locations?.[0] || booking?.location || null,
      customAnswers: reconciledAnswers,
      inviteeName:  this.asString(invitee?.name),
      inviteeEmail: this.asString(invitee?.email),
      inviteePhone: this.asString(invitee?.phone),
      calendarTimezone: this.asString(invitee?.timezone)
    };
  }


  // Coerce any value to a trimmed string. Returns '' for null/undefined so
  // callers can chain with || without worrying about value type. Guards every
  // downstream .trim() call against numeric/object/array fields slipping in.
  private asString(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }


  // EmailAddressService.getEmailFromRecord returns the full email-address OBJECT
  // ({ email_address, primary, invalid_email, ... }), not a string. Pull the actual
  // address string off it so callers can drop it straight into form fields.
  private extractPrimaryEmail(record: any): string {
    const emailObj: any = this._emailAddressService?.getEmailFromRecord(record);
    return emailObj?.email_address || '';
  }


  // Format a record's address as a single human-readable string.
  // Tries the common address fields in priority order. Handles both the standard
  // object shape ({ street, city, state, zip }) used by leads' current_address
  // AND a raw string fallback in case some type stores it as plain text.
  // Every part is funneled through asString to defend against corrupt values.
  private extractAddress(record: any): string {
    if (!record) return '';
    const addr = record?.current_address || record?.mailing_address || record?.address || null;
    if (!addr) return '';
    if (typeof addr === 'string') return addr.trim();
    const street = this.asString(addr?.street) || this.asString(addr?.street1);
    const street2 = this.asString(addr?.street2);
    const city = this.asString(addr?.city);
    const state = this.asString(addr?.state);
    const zip = this.asString(addr?.zip) || this.asString(addr?.zip_code);
    const stateZip = [state, zip].filter(Boolean).join(' ');
    return [street, street2, city, stateZip].filter(Boolean).join(', ');
  }


  private buildFullName(firstName: any, lastName: any, fallback: any): string {
    const first = this.asString(firstName);
    const last = this.asString(lastName);
    const composed = [first, last].filter(Boolean).join(' ');
    return composed || this.asString(fallback);
  }
}